import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/auth/AuthContext";
import { useSearch } from "../components/search/SearchContext";
import { useLicense } from "../licensing/LicenseContext";
import ConfirmModal from "../components/common/ConfirmModal";
import {
  listDocumentRequests,
  getDocumentRequest,
  createDocumentRequest,
  rerunDocumentRequest,
  cancelDocumentRequest,
  confirmDocumentRequest,
  listDeliveryRecipients,
  deliverDocumentRequest,
} from "../api/documentRequestApi";
import "./documentRequests.css";

/**
 * Somebody pastes what was asked for, and the archive answers line by line.
 *
 * <b>The per-line status is the whole screen.</b> "You are missing a current address proof" is the
 * answer somebody actually wants, so every line shows its own outcome and the reason behind it, and
 * the five outcomes are drawn differently on purpose: "we have none", "we have one from 2019", "we
 * have one and nobody has dated it" and "nobody has said what this line means" are four different
 * things to tell a person, and a screen that paints them the same colour is lying to them.
 *
 * <b>It stops at a proposal.</b> There is no confirm and no send here, and the note above the lines
 * says so. The pasted text may be a fraudster's, and the defence is that nothing on this page can move
 * a document to anybody.
 *
 * <b>Bases the scope left out are shown, not hidden.</b> A list of "missing" reads completely
 * differently once it also says three of your five bases were never searched.
 */
/** Maps a server refusal code onto the locale key that explains it in the reader's language. */
function refusalKey(code) {
  switch (code) {
    case "CONFIRM_AGENT_NOT_PERMITTED": return "agentNotPermitted";
    case "CONFIRM_NOT_PROPOSED": return "notProposed";
    case "CONFIRM_NOTHING_SELECTED": return "nothingSelected";
    case "CONFIRM_NOT_IN_PROPOSAL": return "notInProposal";
    case "CONFIRM_UNDER_LEGAL_HOLD": return "underLegalHold";
    case "CONFIRM_CANNOT_VERIFY": return "cannotVerify";
    case "CONFIRM_CONSENT_REQUIRED": return "consentRequired";
    default: return "generic";
  }
}

/** Maps a delivery refusal onto the locale key that explains it. */
function deliverRefusalKey(code) {
  switch (code) {
    case "DELIVER_AGENT_NOT_PERMITTED": return "agentNotPermitted";
    case "DELIVER_NOT_CONFIRMED": return "notConfirmed";
    case "DELIVER_NO_RECIPIENT": return "noRecipient";
    case "DELIVER_NOTHING_CONFIRMED": return "nothingConfirmed";
    case "DELIVER_SEVERAL_BASES": return "severalBases";
    case "DELIVER_NOT_ADDRESSABLE": return "notAddressable";
    case "DELIVER_UNDER_LEGAL_HOLD": return "underLegalHold";
    // NULL, not a generic string. The delivery layer's own DELIVERY_* refusals keep their own
    // message: they are its rules, it explains them better than a guess mapped onto one of ours
    // would, and a catch-all here would replace a precise reason with a vague one.
    default: return null;
  }
}

export default function DocumentRequestsPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { hasFeature, featuresResolved } = useLicense();
  const navigate = useNavigate();
  // Opening a document is the archive's own search path, so the reader's entitlement is re-applied
  // when they arrive. A proposal listing a document is not a key to it.
  const search = useSearch();

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Messages are held as {key, params} and translated at RENDER time. Storing translated strings
  // would need `t` in the callbacks' dependency lists, and useTranslation returns a fresh `t` on
  // every render — which turns the load effect into an infinite loop.
  const [notice, setNotice] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // itemNumber -> {dbName, archiveNo}. One document per line: a line is satisfied by a document, and
  // offering several would invite a selection the server would only refuse.
  const [picks, setPicks] = useState({});
  const [held, setHeld] = useState([]);
  const [consent, setConsent] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientId, setRecipientId] = useState("");
  const [requireCode, setRequireCode] = useState(false);
  // Shown once and never again: the grant row keeps only a hash of it.
  const [delivered, setDelivered] = useState(null);

  const [context, setContext] = useState("Personal");
  const [purpose, setPurpose] = useState("");
  const [subjectKey, setSubjectKey] = useState("");
  const [title, setTitle] = useState("");
  const [ask, setAsk] = useState("");

  const askRef = useRef(null);
  const licensed = hasFeature("ai-retrieval");
  // hasFeature() is optimistic until the licence status lands; fetching on that optimistic answer
  // 403s at every site without ai-retrieval. Render off `licensed`, fetch off this.
  const canFetch = licensed && featuresResolved;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const rows = await listDocumentRequests(token);
      setItems(Array.isArray(rows) ? rows : []);
      setNotice(null);
    } catch (e) {
      setItems([]);
      setNotice({ kind: "error", key: e?.status === 403 ? "documentRequests.notLicensed" : "documentRequests.loadFailed" });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (canFetch) load(); }, [load, canFetch]);

  useEffect(() => {
    if (!canFetch || !token) return;
    let cancelled = false;
    listDeliveryRecipients(token)
      .then((rows) => { if (!cancelled) setRecipients(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setRecipients([]); });
    return () => { cancelled = true; };
  }, [canFetch, token]);

  const openRequest = useCallback(async (requestKey) => {
    if (!token) return;
    setSelected(requestKey);
    setOutcome(null);
    setPicks({});
    setHeld([]);
    setConsent(false);
    setDelivered(null);
    try {
      setDetail(await getDocumentRequest(token, requestKey));
    } catch {
      setDetail(null);
      setNotice({ kind: "error", key: "documentRequests.loadFailed" });
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!purpose.trim()) {
      setNotice({ kind: "error", key: "documentRequests.purposeRequired" });
      return;
    }
    setBusy(true);
    try {
      const created = await createDocumentRequest(token, {
        context,
        purpose: purpose.trim(),
        subjectKey: subjectKey.trim() || null,
        title: title.trim() || null,
        sourceKind: "Email",
        sourceText: ask,
      });
      setOutcome(created);
      setSelected(created?.requestKey || null);
      setDetail(null);
      setNotice(null);
      setPicks({});
      setHeld([]);
      setConsent(false);
      setDelivered(null);
      setAsk("");
      await load();
    } catch (err) {
      setNotice({ kind: "error", key: "documentRequests.createFailed", params: { message: err?.message || "" } });
    } finally {
      setBusy(false);
    }
  };

  const rerun = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      setOutcome(await rerunDocumentRequest(token, selected, {}));
      setDetail(null);
      setPicks({});
      setHeld([]);
      await load();
    } catch (err) {
      setNotice({ kind: "error", key: "documentRequests.createFailed", params: { message: err?.message || "" } });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setConfirming(false);
    if (!selected) return;
    setBusy(true);
    try {
      await cancelDocumentRequest(token, selected, null);
      setOutcome(null);
      await load();
      await openRequest(selected);
    } catch (err) {
      setNotice({ kind: "error", key: "documentRequests.createFailed", params: { message: err?.message || "" } });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!selected) return;
    const documents = Object.entries(picks).map(([itemNumber, doc]) => ({
      itemNumber: Number(itemNumber),
      dbName: doc.dbName,
      archiveNo: doc.archiveNo,
    }));
    if (documents.length === 0) return;

    setBusy(true);
    setHeld([]);
    try {
      const result = await confirmDocumentRequest(token, selected, documents, consent);
      setOutcome(null);
      setDetail(null);
      setNotice(null);
      setPicks({});
      await load();
      await openRequest(result?.requestKey || selected);
    } catch (err) {
      // The server's refusal codes are a closed set, so they are translated here rather than shown
      // in whatever language the API happens to speak. The server's own message is the fallback.
      setHeld(err?.details?.held || []);
      setNotice({
        kind: "error",
        key: `documentRequests.refusal.${refusalKey(err?.code)}`,
        params: { defaultValue: err?.message || "" },
      });
    } finally {
      setBusy(false);
    }
  };

  const deliver = async () => {
    if (!selected || !recipientId) return;
    setBusy(true);
    setHeld([]);
    try {
      const result = await deliverDocumentRequest(token, selected, {
        recipientId: Number(recipientId),
        requireAccessCode: requireCode,
      });
      setDelivered(result);
      setNotice(null);
      await load();
    } catch (err) {
      setHeld(err?.details?.held || []);
      const mapped = deliverRefusalKey(err?.code);
      setNotice(mapped
        ? { kind: "error", key: `documentRequests.deliverRefusal.${mapped}` }
        // Nothing of ours to say: show what the archive said, verbatim.
        : { kind: "error", text: err?.message, key: "documentRequests.deliverRefusal.generic" });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Opens one of the proposed documents.
   *
   * It goes through the archive's ordinary search rather than a direct address, which is the point:
   * the reader's own entitlement is re-applied when they arrive. A proposal that lists a document is
   * telling somebody it exists, not handing them a key to it — the same rule the shared-with-me inbox
   * follows.
   */
  const openDocument = (match) => {
    if (!match?.dbName || !match?.archiveNo) return;
    if (search?.runSearch) search.runSearch(`${match.dbName} ${match.archiveNo}`);
    navigate("/");
  };

  const when = (iso) => { try { return iso ? new Date(iso).toLocaleString() : ""; } catch { return ""; } };
  const day = (iso) => { try { return iso ? new Date(iso).toLocaleDateString() : "—"; } catch { return "—"; } };

  const statusLabel = (s) =>
    t(`documentRequests.status.${(s || "Draft").toLowerCase()}`, { defaultValue: s || "" });
  const lineStatusLabel = (s) =>
    t(`documentRequests.lineStatus.${(s || "Pending").toLowerCase()}`, { defaultValue: s || "" });

  // The current view is whichever is fresher: a request just run, or one opened from the list.
  const view = useMemo(() => {
    if (outcome && (!selected || outcome.requestKey === selected)) {
      return {
        requestKey: outcome.requestKey,
        status: outcome.status,
        purpose: outcome.purpose,
        subjectKey: outcome.subjectKey,
        context: outcome.context,
        refusalCode: outcome.scopeRefusalCode,
        refusalMessage: outcome.scopeRefusalMessage,
        revision: outcome.revision,
        lines: outcome.lines || [],
        excluded: outcome.excluded || [],
        confirmedBy: null,
        confirmedAt: null,
      };
    }
    if (detail) {
      return {
        requestKey: detail.requestKey,
        status: detail.status,
        purpose: detail.purpose,
        subjectKey: detail.subjectKey,
        context: detail.context,
        refusalCode: detail.currentRevision?.scopeRefusalCode,
        refusalMessage: detail.closedReason,
        revision: detail.currentRevision,
        revisions: detail.revisions || [],
        lines: detail.lines || [],
        excluded: [],
        confirmedBy: detail.confirmedBy,
        confirmedAt: detail.confirmedAt,
      };
    }
    return null;
  }, [outcome, detail, selected]);

  // Only a live proposal carries its candidates: the stored detail records the outcome per line, not
  // the list it chose from. Re-running is how you see them again, and seeing them again is itself a
  // disclosure the archive logs — which is why they are not quietly re-fetched on every page view.
  const isProposed = view?.status === "Proposed";
  const hasCandidates = (view?.lines || []).some((l) => Array.isArray(l.matches) && l.matches.length > 0);
  const selectable = isProposed && hasCandidates;
  const deliverable = view?.status === "Confirmed";
  const proposedWithoutCandidates = isProposed && !hasCandidates;
  // Only asked when a CHOSEN line needs it: a standing checkbox on every proposal is a thing people
  // learn to tick without reading.
  const needsConsent = (view?.lines || []).some(
    (l) => picks[l.itemNumber] && l.status === "NeedsConsent");

  if (!licensed) {
    return (
      <div className="dr">
        <div className="dr-empty">
          {t("documentRequests.notLicensed", {
            defaultValue: "Document requests are part of the AI retrieval feature, which is not enabled on this licence.",
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="dr">
      <div className="dr-head">
        <h1 className="dr-title">{t("documentRequests.title", { defaultValue: "Document requests" })}</h1>
        <p className="dr-sub">
          {t("documentRequests.subtitle", {
            defaultValue: "Paste what was asked for, and see what the archive has, line by line.",
          })}
        </p>
      </div>

      {notice && (
        <div className={`dr-notice dr-${notice.kind}`}>
          {notice.text || t(notice.key, { defaultValue: "Something went wrong.", ...(notice.params || {}) })}
        </div>
      )}

      <div className="dr-grid">
        <div className="dr-col">
          <form className="dr-card dr-form" onSubmit={submit}>
            <h2 className="dr-card-title">{t("documentRequests.newRequest", { defaultValue: "New request" })}</h2>

            <label className="dr-label" htmlFor="dr-context">
              {t("documentRequests.context", { defaultValue: "Whose documents" })}
            </label>
            <select id="dr-context" className="dr-input" value={context} onChange={(e) => setContext(e.target.value)}>
              <option value="Personal">{t("documentRequests.contextPersonal", { defaultValue: "My own filings" })}</option>
              <option value="Delegated">{t("documentRequests.contextDelegated", { defaultValue: "Somebody else's records" })}</option>
            </select>

            {context === "Delegated" && (
              <>
                <label className="dr-label" htmlFor="dr-subject">
                  {t("documentRequests.subject", { defaultValue: "Subject" })}
                </label>
                <input
                  id="dr-subject"
                  className="dr-input"
                  value={subjectKey}
                  onChange={(e) => setSubjectKey(e.target.value)}
                  placeholder={t("documentRequests.subjectPlaceholder", { defaultValue: "Customer number or identifier" })}
                />
              </>
            )}

            <label className="dr-label" htmlFor="dr-purpose">
              {t("documentRequests.purpose", { defaultValue: "Purpose" })} <span className="dr-req">*</span>
            </label>
            <input
              id="dr-purpose"
              className="dr-input"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder={t("documentRequests.purposePlaceholder", { defaultValue: "Why these documents are being assembled" })}
            />
            <p className="dr-hint">
              {t("documentRequests.purposeHint", {
                defaultValue: "Required. This is the lawful-basis record, and every document this request shows you is logged against it.",
              })}
            </p>

            <label className="dr-label" htmlFor="dr-titlefield">
              {t("documentRequests.titleField", { defaultValue: "Title" })}
            </label>
            <input
              id="dr-titlefield"
              className="dr-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("documentRequests.titlePlaceholder", { defaultValue: "Optional short name" })}
            />

            <label className="dr-label" htmlFor="dr-ask">
              {t("documentRequests.ask", { defaultValue: "What was asked for" })}
            </label>
            <textarea
              id="dr-ask"
              ref={askRef}
              className="dr-input dr-textarea"
              rows={7}
              value={ask}
              onChange={(e) => setAsk(e.target.value)}
              placeholder={t("documentRequests.askPlaceholder", { defaultValue: "Paste the email or list the documents…" })}
            />
            <p className="dr-hint">
              {t("documentRequests.askHint", {
                defaultValue: "Stored exactly as received, as the record of what was asked. It is never treated as an instruction.",
              })}
            </p>

            <button className="dr-btn dr-primary" type="submit" disabled={busy}>
              {busy
                ? t("common.creating", { defaultValue: "Creating…" })
                : t("documentRequests.create", { defaultValue: "Ask the archive" })}
            </button>
          </form>

          <div className="dr-card">
            <h2 className="dr-card-title">{t("documentRequests.mine", { defaultValue: "My requests" })}</h2>
            {loading && <div className="dr-muted">{t("common.loading", { defaultValue: "Loading…" })}</div>}
            {!loading && items.length === 0 && (
              <div className="dr-muted">{t("documentRequests.empty", { defaultValue: "You have not raised any requests yet." })}</div>
            )}
            <ul className="dr-list">
              {items.map((r) => (
                <li key={r.requestKey}>
                  <button
                    type="button"
                    className={`dr-row${selected === r.requestKey ? " sel" : ""}`}
                    onClick={() => openRequest(r.requestKey)}
                  >
                    <span className="dr-key">{r.requestKey}</span>
                    <span className={`dr-badge s-${(r.status || "").toLowerCase()}`}>{statusLabel(r.status)}</span>
                    <span className="dr-row-title">{r.title || r.purpose}</span>
                    <span className="dr-when">{when(r.createdAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="dr-col dr-wide">
          {!view && (
            <div className="dr-card dr-empty">
              {t("documentRequests.pickOne", { defaultValue: "Raise a request, or pick one from the list." })}
            </div>
          )}

          {view && (
            <div className="dr-card">
              <div className="dr-detail-head">
                <span className="dr-key">{view.requestKey}</span>
                <span className={`dr-badge s-${(view.status || "").toLowerCase()}`}>{statusLabel(view.status)}</span>
                <span className="dr-spacer" />
                <button type="button" className="dr-btn" onClick={rerun} disabled={busy}>
                  {t("documentRequests.rerun", { defaultValue: "Run again" })}
                </button>
                <button type="button" className="dr-btn dr-danger" onClick={() => setConfirming(true)} disabled={busy}>
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
              </div>

              <div className="dr-meta">
                <div><span className="dr-meta-k">{t("documentRequests.purpose", { defaultValue: "Purpose" })}</span> {view.purpose}</div>
                {view.subjectKey && (
                  <div><span className="dr-meta-k">{t("documentRequests.subject", { defaultValue: "Subject" })}</span> {view.subjectKey}</div>
                )}
                {view.revision && (
                  <div>
                    <span className="dr-meta-k">{t("documentRequests.revision", { defaultValue: "Attempt" })}</span>
                    {view.revision.revisionNumber}
                    {view.revision.scopeResolverVersion
                      ? ` · ${t("documentRequests.rules", { defaultValue: "rules" })} v${view.revision.scopeResolverVersion}`
                      : ""}
                  </div>
                )}
              </div>

              {view.confirmedBy && (
                <div className="dr-confirmed">
                  <strong>
                    {t("documentRequests.confirmedBy", {
                      defaultValue: "Confirmed by {{user}}",
                      user: view.confirmedBy,
                    })}
                  </strong>
                  <span className="dr-when">{when(view.confirmedAt)}</span>
                </div>
              )}

              {deliverable && !delivered && (
                <div className="dr-deliver">
                  <h3 className="dr-card-title">
                    {t("documentRequests.deliverTitle", { defaultValue: "Send this set" })}
                  </h3>

                  <label className="dr-label" htmlFor="dr-recipient">
                    {t("documentRequests.recipient", { defaultValue: "Recipient" })}
                  </label>
                  <select
                    id="dr-recipient"
                    className="dr-input"
                    value={recipientId}
                    onChange={(e) => setRecipientId(e.target.value)}
                  >
                    <option value="">
                      {t("documentRequests.recipientChoose", { defaultValue: "Choose a recipient…" })}
                    </option>
                    {recipients.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.displayName}{r.externalRef ? ` · ${r.externalRef}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="dr-hint">
                    {t("documentRequests.recipientHint", {
                      defaultValue:
                        "Recipients are the people this organisation already knows. A destination is never taken from the text of the request.",
                    })}
                  </p>

                  {recipients.length === 0 && (
                    <p className="dr-hint">
                      {t("documentRequests.noRecipients", {
                        defaultValue: "No recipients are set up. An administrator adds them before anything can be sent.",
                      })}
                    </p>
                  )}

                  <label className="dr-check">
                    <input
                      type="checkbox"
                      checked={requireCode}
                      onChange={(e) => setRequireCode(e.target.checked)}
                    />
                    {t("documentRequests.requireAccessCode", { defaultValue: "Ask for an access code" })}
                  </label>

                  <button
                    type="button"
                    className="dr-btn dr-primary dr-inline"
                    onClick={deliver}
                    disabled={busy || !recipientId}
                  >
                    {t("documentRequests.deliver", { defaultValue: "Send" })}
                  </button>
                </div>
              )}

              {delivered && (
                <div className="dr-delivered">
                  <strong>{t("documentRequests.deliveredTitle", { defaultValue: "Sent" })}</strong>
                  <p className="dr-once">
                    {t("documentRequests.tokenOnce", {
                      defaultValue:
                        "This link is shown once and is not stored anywhere it can be read back. Copy it now; if it is lost, send the set again.",
                    })}
                  </p>
                  <code className="dr-token">{delivered.token}</code>
                  {delivered.expiresAt && (
                    <p className="dr-hint">
                      {t("documentRequests.expiresAt", {
                        defaultValue: "Valid until {{when}}",
                        when: when(delivered.expiresAt),
                      })}
                    </p>
                  )}
                </div>
              )}

              {held.length > 0 && (
                <div className="dr-held">
                  <strong>
                    {t("documentRequests.heldTitle", {
                      defaultValue: "{{count}} of the selected document(s) are under a legal hold",
                      count: held.length,
                    })}
                  </strong>
                  <ul>
                    {held.map((h) => (
                      <li key={`${h.dbName}/${h.archiveNo}`}>
                        <span className="dr-arc">{h.archiveNo}</span>
                        <span className="dr-db">{h.dbName}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="dr-hint">
                    {t("documentRequests.heldNote", {
                      defaultValue: "The whole confirmation was refused rather than quietly leaving them out.",
                    })}
                  </p>
                </div>
              )}

              {view.refusalCode && (
                <div className="dr-refused">
                  <strong>{t("documentRequests.refusedTitle", { defaultValue: "This request was refused" })}</strong>
                  <div className="dr-refusal-code">{view.refusalCode}</div>
                  {view.refusalMessage && <p>{view.refusalMessage}</p>}
                </div>
              )}

              {view.excluded && view.excluded.length > 0 && (
                <div className="dr-excluded">
                  <strong>
                    {t("documentRequests.excludedTitle", {
                      defaultValue: "{{count}} document base(s) were not searched",
                      count: view.excluded.length,
                    })}
                  </strong>
                  <ul>
                    {view.excluded.map((x) => (
                      <li key={x.dbName}>
                        <span className="dr-db">{x.dbName}</span>
                        <span className="dr-reason-code">{x.reason}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="dr-hint">
                    {t("documentRequests.excludedNote", {
                      defaultValue: "An answer of \"missing\" below only covers the bases that were actually searched.",
                    })}
                  </p>
                </div>
              )}

              {view.lines.length > 0 && (
                <>
                  <p className="dr-proposal-note">
                    {t("documentRequests.proposalNote", {
                      defaultValue: "This is a proposal. Nothing has been sent to anybody, and nothing will be until a person decides to.",
                    })}
                  </p>

                  <ul className="dr-lines">
                    {view.lines.map((line) => (
                      <li key={line.itemNumber} className={`dr-line l-${(line.status || "").toLowerCase()}`}>
                        <div className="dr-line-head">
                          <span className="dr-line-no">{line.itemNumber}</span>
                          <span className={`dr-badge l-${(line.status || "").toLowerCase()}`}>
                            {lineStatusLabel(line.status)}
                          </span>
                          <span className="dr-line-text">{line.requirementText}</span>
                        </div>
                        {line.statusReason && <div className="dr-line-reason">{line.statusReason}</div>}
                        {Array.isArray(line.matches) && line.matches.length > 0 && (
                          <ul className="dr-matches">
                            {line.matches.map((m) => (
                              <li key={`${m.dbName}/${m.archiveNo}`}>
                                {selectable && (
                                  <input
                                    type="radio"
                                    name={`pick-${line.itemNumber}`}
                                    checked={picks[line.itemNumber]?.archiveNo === m.archiveNo &&
                                             picks[line.itemNumber]?.dbName === m.dbName}
                                    onChange={() => setPicks((p) => ({
                                      ...p,
                                      [line.itemNumber]: { dbName: m.dbName, archiveNo: m.archiveNo },
                                    }))}
                                    aria-label={t("documentRequests.selectDocument", {
                                      defaultValue: "Include {{archiveNo}}",
                                      archiveNo: m.archiveNo,
                                    })}
                                  />
                                )}
                                <button
                                  type="button"
                                  className="dr-open"
                                  onClick={() => openDocument(m)}
                                  aria-label={t("documentRequests.openDocument", {
                                    defaultValue: "Open {{archiveNo}}",
                                    archiveNo: m.archiveNo,
                                  })}
                                >
                                  {m.archiveNo}
                                </button>
                                <span className="dr-db">{m.dbName}</span>
                                <span className="dr-date">
                                  {t("documentRequests.dated", { defaultValue: "dated" })} {day(m.effectiveDate)}
                                </span>
                                {m.issuerName && <span className="dr-issuer">{m.issuerName}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>

                  {selectable && needsConsent && (
                    <label className="dr-check dr-consent">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                      />
                      {t("documentRequests.consentObtained", {
                        defaultValue:
                          "One of these may not be handed over without the subject's consent. I confirm it has been obtained.",
                      })}
                    </label>
                  )}

                  {selectable && (
                    <div className="dr-confirm-bar">
                      <button
                        type="button"
                        className="dr-btn dr-primary dr-inline"
                        onClick={confirm}
                        disabled={busy || Object.keys(picks).length === 0}
                      >
                        {t("documentRequests.confirm", { defaultValue: "Confirm this set" })}
                      </button>
                      <p className="dr-hint">
                        {t("documentRequests.confirmHint", {
                          defaultValue:
                            "Confirming records that you decided these documents go. It still sends nothing.",
                        })}
                      </p>
                    </div>
                  )}

                  {proposedWithoutCandidates && (
                    <p className="dr-hint dr-rerun-hint">
                      {t("documentRequests.rerunToConfirm", {
                        defaultValue:
                          "Run this request again to see the documents it found and choose which ones go.",
                      })}
                    </p>
                  )}
                </>
              )}

              {view.lines.length === 0 && !view.refusalCode && (
                <div className="dr-muted">
                  {t("documentRequests.noLines", {
                    defaultValue: "Nothing in this request matched a document the rules know how to look for.",
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirming}
        variant="danger"
        title={t("documentRequests.cancelTitle", { defaultValue: "Cancel this request?" })}
        name={selected || ""}
        body={t("documentRequests.cancelBody", {
          defaultValue: "The request and every attempt stay on record. It simply stops being open.",
        })}
        confirmLabel={t("documentRequests.cancelConfirm", { defaultValue: "Cancel the request" })}
        onConfirm={doCancel}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
